import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { submitIntake } from '../api/client';
import { Button } from '../components/ui/Button';
import toast from 'react-hot-toast';

const PARTNERSHIP_TYPES = [
  'Strategic Partnership',
  'Reseller / Distribution',
  'Hosting Partnership',
  'Technical Collaboration',
  'Marketing / Co-branding',
  'Other',
] as const;

const INDUSTRIES = [
  'Technology',
  'Finance',
  'Healthcare',
  'Retail',
  'Local Business',
  'Government / NGO',
  'Other',
] as const;

const cardVariants = {
  hidden: { opacity: 0, y: 32, scale: 0.97 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { delay: i * 0.06, duration: 0.35, type: 'spring', bounce: 0.2 },
  }),
};

const PartnershipApplication: React.FC = () => {
  const [form, setForm] = useState({
    name: '',
    contact: '',
    website: '',
    partnershipType: '',
    industry: '',
    details: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitIntake({ type: 'partnership', ...form });
      setSubmitted(true);
      toast.success('Partnership application submitted!');
    } catch (err: any) {
      const msg =
        err?.response?.data?.errors?.join(', ') ||
        err?.response?.data?.error ||
        'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="page-shell page-section-major">
        <div className="page-inner">
          <motion.div
            className="rounded-2xl p-px overflow-hidden"
            style={{ background: 'rgba(34,197,94,0.25)', boxShadow: '0 12px 36px rgba(2,6,23,0.45)' }}
            initial="hidden"
            animate="visible"
            custom={0}
            variants={cardVariants}
          >
            <div className="rounded-2xl bg-secondary-900 border border-success-500/25 p-7 sm:p-10 text-center">
              <div className="w-14 h-14 bg-success-500/20 border border-success-500/35 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-success-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-success-300 mb-4">Application Received</h2>
              <p className="text-secondary-300 mb-6 leading-loose">
                Thank you for your partnership application. We will review it and reach out to you
                directly at the contact address you provided.
              </p>
              <Button to="/services" size="lg">
                Back to Services
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          'radial-gradient(ellipse 90% 55% at 20% 0%, rgba(56,189,248,0.12) 0%, transparent 62%), radial-gradient(ellipse 70% 45% at 85% 10%, rgba(217,70,239,0.10) 0%, transparent 62%)',
      }}
    >
      <section className="page-shell page-section-major animate-fade-in">
        <div className="page-inner">
          <div className="text-center mb-10 sm:mb-12">
            <p className="text-xs font-medium tracking-[0.24em] uppercase mb-4 text-primary-300">Partnership forum</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              <span className="cw">Partnership </span>
              <span className="cb">Application</span>
            </h1>
            <p className="text-base sm:text-lg text-secondary-300 leading-loose">
              Interested in partnering with FairCorp? Fill out the form below and we will be in touch.
            </p>
          </div>

          <motion.div
            className="rounded-2xl p-px overflow-hidden"
            style={{ background: 'rgba(14,165,233,0.24)', boxShadow: '0 14px 38px rgba(8,47,73,0.35)' }}
            initial="hidden"
            animate="visible"
            custom={0}
            variants={cardVariants}
          >
            <div className="rounded-2xl p-7 sm:p-9 border border-primary-500/25 bg-secondary-900">
              <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-primary-300 mb-2">
              Company / Organization Name <span className="text-error-400">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              maxLength={150}
              value={form.name}
              onChange={handleChange}
              className="w-full min-h-[44px] px-4 py-3 bg-secondary-900/80 border border-primary-500/25 rounded-xl text-secondary-100 placeholder-secondary-500 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
              placeholder="Acme Corp"
            />
          </div>

          <div>
            <label htmlFor="contact" className="block text-sm font-medium text-primary-300 mb-2">
              Contact Email <span className="text-error-400">*</span>
            </label>
            <input
              id="contact"
              name="contact"
              type="email"
              required
              maxLength={150}
              value={form.contact}
              onChange={handleChange}
              className="w-full min-h-[44px] px-4 py-3 bg-secondary-900/80 border border-primary-500/25 rounded-xl text-secondary-100 placeholder-secondary-500 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
              placeholder="partner@example.com"
            />
          </div>

          <div>
            <label htmlFor="website" className="block text-sm font-medium text-primary-300 mb-2">
              Website
            </label>
            <input
              id="website"
              name="website"
              type="text"
              maxLength={150}
              value={form.website}
              onChange={handleChange}
              className="w-full min-h-[44px] px-4 py-3 bg-secondary-900/80 border border-primary-500/25 rounded-xl text-secondary-100 placeholder-secondary-500 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
              placeholder="https://example.com"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="partnershipType" className="block text-sm font-medium text-primary-300 mb-2">
                Partnership Type
              </label>
              <select
                id="partnershipType"
                name="partnershipType"
                value={form.partnershipType}
                onChange={handleChange}
                className="w-full min-h-[44px] px-4 py-3 bg-secondary-900/80 border border-primary-500/25 rounded-xl text-secondary-100 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
              >
                <option value="">Select type...</option>
                {PARTNERSHIP_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="industry" className="block text-sm font-medium text-primary-300 mb-2">
                Industry
              </label>
              <select
                id="industry"
                name="industry"
                value={form.industry}
                onChange={handleChange}
                className="w-full min-h-[44px] px-4 py-3 bg-secondary-900/80 border border-primary-500/25 rounded-xl text-secondary-100 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
              >
                <option value="">Select industry...</option>
                {INDUSTRIES.map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="details" className="block text-sm font-medium text-primary-300 mb-2">
              Proposal Details <span className="text-error-400">*</span>
            </label>
            <textarea
              id="details"
              name="details"
              required
              maxLength={2000}
              rows={7}
              value={form.details}
              onChange={handleChange}
              className="w-full min-h-[44px] px-4 py-3.5 bg-secondary-900/80 border border-primary-500/25 rounded-xl text-secondary-100 placeholder-secondary-500 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-y"
              placeholder="Describe the partnership opportunity and how our organizations could collaborate..."
            />
          </div>

          <Button type="submit" fullWidth size="lg" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Application'}
          </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PartnershipApplication;
